const getEnvironmentConfig = () => {
  return {
    API_URL: process.env.REACT_APP_API_URL || 'http://localhost:3001',
    NODE_ENV: process.env.REACT_APP_NODE_ENV || 'development',
    IS_PRODUCTION: process.env.REACT_APP_IS_PRODUCTION === 'true'
  };
};

export const CONFIG = getEnvironmentConfig();

// Politique météo Magic Park Times :
// - Pour tous les parcs hors France, Open-Meteo est la source principale (fiable, gratuite, quotas élevés).
// - AccuWeather n'est utilisé qu'en fallback si Open-Meteo échoue (erreur réseau, quota, etc).
// - Pour les parcs français, Open-Meteo est aussi la source principale (depuis 2025-05-16).
// Cette règle s'applique côté serveur (voir magic-park-times-server/server.js).
//
// Toute référence à AccuWeather côté front doit être considérée comme obsolète.

// Configuration météo
export const WEATHER_CONFIG = {
  THRESHOLDS: {
    WIND: 70,      // km/h vent moyen pour les attractions
    GUST: 90,      // km/h rafales maximales pour les attractions
    // Seuils drones ajustés le 26/11/2025 après analyse du 25/11 (spectacle annulé malgré conditions "favorables")
    // Le 25/11 à 22h : vent 14 km/h, rafales 24 km/h, 0 mm pluie, mais spectacle annulé
    // Causes probables : pluie dans les heures précédentes (10h-14h), humidité élevée (87-95%), température basse (4°C)
    DRONE: 25,     // km/h vent moyen pour les drones (était 38, réduit car trop permissif)
    DRONE_GUST: 40, // km/h rafales maximales pour les drones (était 55, réduit car trop permissif)
    RAIN: 0.5,     // mm de pluie (était 8, réduit car même une pluie légère peut annuler)
    DRONE_HUMIDITY: 98, // % humidité relative maximale pour les drones (était 95, ajusté le 06/01/2026 car drone a volé le 05/01 avec 97%)
    DRONE_TEMP_MIN: -5, // °C température minimale pour les drones (était 2, ajusté le 06/01/2026 car drone a volé à -3°C le 04/01)
    DRONE_SNOW: 0,      // mm de neige maximum si température < 0°C (ajouté le 10/01/2026)
    DRONE_SNOW_WARM: 1, // mm de neige maximum si température >= 0°C (neige fondue, ajouté le 10/01/2026)
  },
  // Marge d'erreur pour les prédictions drones (différence entre station météo et position réelle du château)
  // Ajouté le 12/02/2026 suite aux observations : les rafales réelles peuvent être supérieures à celles prévues
  // Marges augmentées le 12/02/2026 pour être plus pessimiste (mieux vaut annoncer non OK et avoir un OK réel)
  DRONE_MARGIN: {
    WIND_PERCENT: 25,  // 25% de marge pour le vent moyen (plus pessimiste)
    GUST_PERCENT: 30,  // 30% de marge pour les rafales (plus pessimiste - principal facteur d'annulation)
  },
  REFRESH_INTERVAL: 15 * 60 * 1000, // 15 minutes
  DEFAULT_SHOW_TIME: '22:40',
  WEATHER_CODES: {
    THUNDERSTORM: [100, 101, 102, 103, 104, 105, 106, 107, 108],
    THUNDERSTORM_WITH_HAIL: [120, 121, 122, 123, 124, 125, 126, 127, 128],
    THUNDERSTORM_WITH_SNOW: [130, 131, 132, 133, 134, 135, 136, 137, 138],
    STORMY_RAIN: [140],
    HAIL: [235],
    HEAVY_RAIN: [132, 133, 134, 135, 136, 137, 138, 140, 141, 142]
  }
};

// Exporter la configuration complète
export default {
  ...CONFIG,
  WEATHER_CONFIG
};

// DLPWait backend API (full dataset)
export const DLPWAIT_FULL_API = `${CONFIG.API_URL}/api/dlpwait/full`;

// Universal Orlando Resort APIs
export const UNIVERSAL_STUDIOS_WAIT_TIMES_API = `${CONFIG.API_URL}/api/universal-studios/wait-times`;
export const ISLANDS_OF_ADVENTURE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/islands-of-adventure/wait-times`;
export const EPIC_UNIVERSE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/epic-universe/wait-times`;

// Universal Studios California API
export const UNIVERSAL_STUDIOS_CALIFORNIA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/universal-studios-california/wait-times`;

// Disneyland Paris APIs
export const WALT_DISNEY_STUDIOS_WAIT_TIMES_API = `${CONFIG.API_URL}/api/walt-disney-studios/wait-times`;
export const DISNEYLAND_PARK_WAIT_TIMES_API = `${CONFIG.API_URL}/api/disneyland-park/wait-times`;

// Disneyland Resort Californie
export const DISNEYLAND_CALIFORNIA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/disneyland-california/wait-times`;
export const DISNEY_CALIFORNIA_ADVENTURE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/disney-california-adventure/wait-times`;

// Walt Disney World Resort
export const MAGIC_KINGDOM_WAIT_TIMES_API = `${CONFIG.API_URL}/api/magic-kingdom/wait-times`;
export const EPCOT_WAIT_TIMES_API = `${CONFIG.API_URL}/api/epcot/wait-times`;
export const HOLLYWOOD_STUDIOS_WAIT_TIMES_API = `${CONFIG.API_URL}/api/hollywood-studios/wait-times`;
export const ANIMAL_KINGDOM_WAIT_TIMES_API = `${CONFIG.API_URL}/api/animal-kingdom/wait-times`;
export const TYPHOON_LAGOON_WAIT_TIMES_API = `${CONFIG.API_URL}/api/typhoon-lagoon/wait-times`;
export const BLIZZARD_BEACH_WAIT_TIMES_API = `${CONFIG.API_URL}/api/blizzard-beach/wait-times`;

// APIs
export const PORTAVENTURA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/portaventura/wait-times`;
export const FERRARI_LAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/ferrari-land/wait-times`;

// Knott's Berry Farm
export const KNOTTS_BERRY_FARM_WAIT_TIMES_API = `${CONFIG.API_URL}/api/knotts-berry-farm/wait-times`;

// Park IDs
export const PARK_IDS = {
  UNIVERSAL_STUDIOS: 'usf',
  ISLANDS_OF_ADVENTURE: 'ioa',
  EPIC_UNIVERSE: 'epu',
  UNIVERSAL_STUDIOS_CALIFORNIA: 'usc',
  SEAWORLD_ORLANDO: 'seaworld-orlando',
  BUSCH_GARDENS_TAMPA: 'busch-gardens-tampa',
  LEGOLAND_FLORIDA: 'legoland-florida',
  TOVERLAND: 'toverland',
  PORTAVENTURA: 'portaventura',
  FERRARI_LAND: 'ferrariLand',
  KNOTTS_BERRY_FARM: 'b1444147-b93a-4f73-b12d-28f9b1f7ec7c'
};

// IDs des parcs pour l'API ThemeParks
export const THEME_PARKS_IDS = {
  PARC_ASTERIX: '9e938687-fd99-46f3-986a-1878210378f8',
  DISNEYLAND_CALIFORNIA: '7340550b-c14d-4def-80bb-acdb51d49a66',
  DISNEY_CALIFORNIA_ADVENTURE: '832fcd51-ea19-4e77-85c7-75d5843b127c',
  EFTELING: '1f1a4804-9f91-4325-a1ee-1b628fd5cf4b',
  KNOTTS_BERRY_FARM: 'b1444147-b93a-4f73-b12d-28f9b1f7ec7c',
  KNOTTS_BERRY_FARM_SCHEDULE: '0a6123bb-1e8c-4b18-a2d3-2696cf2451f5',
  // Walt Disney World Resort
  MAGIC_KINGDOM: '75ea578a-adc8-4116-a54d-dccb60765ef9',
  EPCOT: 'a7c2b352-a045-49c0-bd7e-c926d5b7dbe0',
  HOLLYWOOD_STUDIOS: '288747d1-8b4f-4a64-867e-ea7c9b27bad8',
  ANIMAL_KINGDOM: '1c84a229-8862-4648-9c71-378ddd2c7693',
  TYPHOON_LAGOON: 'b08ef347-c4b1-45d3-8e1e-3aaa5b3ed0bc',
  BLIZZARD_BEACH: '229a4f99-5987-4b8a-be1f-ed23016fd208'
};

// ID ThemeParks pour Phantasialand (utilisé par l'Option A via apiWrapper.fetchSchedule)
export const PHANTASIALAND_ENTITY_ID = '0257ff9f-c73c-4855-b5b4-774755c4d146';

// Park Parent IDs pour filtrer les données de l'API
export const PARK_PARENT_IDS = {
  UNIVERSAL_STUDIOS: 'eb3f4560-2383-4a36-9152-6b3e5ed6bc57',
  ISLANDS_OF_ADVENTURE: '267615cc-8943-4c2a-ae2c-5da728ca591f',
  EPIC_UNIVERSE: '12dbb85b-265f-44e6-bccf-f1faa17211fc',
  UNIVERSAL_STUDIOS_CALIFORNIA: '9fc68f1c-3f5e-4f09-89f2-aab2cf1a0741',
  SEAWORLD_ORLANDO: '643e837e-b244-4663-8d3a-148c26ecba9c',
  BUSCH_GARDENS_TAMPA: '1d92560c-474f-4425-906d-e9dd2f2da6ca',
  LEGOLAND_FLORIDA: '7a4adf8d-8c3f-4300-b277-19707e4f8e12',
  TOVERLAND: 'c11ad942-8f5d-4642-862c-30d8b7a414e3'
};

// Liste des attractions à exclure
export const EXCLUDED_ATTRACTIONS = [
  'Me Ship, The Olive®',
  'If I Ran The Zoo™',
  'Camp Jurassic™',
  'Illumination Theater',
  'Jurassic Park Discovery Center™',
  // Fermetures définitives
  'Hollywood Rip Ride Rockit™'
];

// Liste des attractions d'Halloween
export const HALLOWEEN_ATTRACTIONS = [
  'Insidious: The Further',
  'Ghostbusters: Frozen Empire',
  'A Quiet Place',
  'Slaughter Sinema 2',
  'Monstruos: The Monsters of Latin America',
  'Triplets of Terror',
  'Major Sweets Candy Factory',
  "Goblin's Feast",
  'The Museum: Deadly Exhibits',
  'Universal Monsters: Eternal Bloodlines',
  // Universal Studios California Halloween attractions
  'Monstruos 3',
  'Scarecrow: Music by Slash',
  'Terrifier',
  'Fallout',
  'Terror Tram: Blumhouse',
  'Jason Universe',
  'JASON UN1V3RSE',
  'Five Nights at Freddy\'s',
  'Five Nights At Freddy\'s',
  'Five Nights At Freddy’s',
  'WWE Wyatt Sicks',
  'WWE Presents: The Horrors of the Wyatt Sicks',
  'Poltergeist',
  'Chainsaw Man',
  'Grave of Flesh',
  'Dolls: Let\'s Play Dead',
  'Dolls: Let’s Play Dead',
  'El Artista: A Spanish Haunting',
  'Hatchet and Chains: Demon Bounty Hunters',
  'Galkn: Monsters of the North',
  'Gálkn: Monsters of the North'
];

// Parc Astérix
export const PARC_ASTERIX_WAIT_TIMES_API = `${CONFIG.API_URL}/api/parc-asterix/wait-times`;

// Efteling
export const EFTELING_WAIT_TIMES_API = `${CONFIG.API_URL}/api/efteling/wait-times`;

// Phantasialand
export const PHANTASIALAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/phantasialand/wait-times`;

// SeaWorld Orlando
export const SEAWORLD_ORLANDO_WAIT_TIMES_API = `${CONFIG.API_URL}/api/seaworld-orlando/wait-times`;

// Busch Gardens Tampa
export const BUSCH_GARDENS_TAMPA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/busch-gardens-tampa/wait-times`;

// Legoland Florida
export const LEGOLAND_FLORIDA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/legoland-florida/wait-times`;

// Toverland
export const TOVERLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/toverland/wait-times`;

// Futuroscope
export const FUTUROSCOPE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/futuroscope/wait-times`;
export const FUTUROSCOPE_SCHEDULE_API = `${CONFIG.API_URL}/api/futuroscope/schedule`;

// Puy du Fou
export const PUY_DU_FOU_SCHEDULE_API = `${CONFIG.API_URL}/api/puydufou/schedule`;
export const PUY_DU_FOU_MAP_API = `${CONFIG.API_URL}/api/puydufou/map`;
export const PUY_DU_FOU_RESTAURANTS_API = `${CONFIG.API_URL}/api/puydufou/restaurants`;
export const PUY_DU_FOU_CROWD_PIVOT_API = `${CONFIG.API_URL}/api/puydufou/crowd/pivot`;
export const PUY_DU_FOU_CROWD_LIVE_API = `${CONFIG.API_URL}/api/puydufou/crowd/live`;
export const PUY_DU_FOU_MAP_TILE_TEMPLATE = `${CONFIG.API_URL}/api/puydufou/map/tiles/{z}/{x}/{y}.jpg`;

// Le Pal
export const LE_PAL_WAIT_TIMES_API = `${CONFIG.API_URL}/api/le-pal/wait-times`;

// Energylandia
export const ENERGYLANDIA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/energylandia/wait-times`;

// Grona Lund
export const GRONA_LUND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/grona-lund/wait-times`;

// Walibi Belgium
export const WALIBI_BELGIUM_WAIT_TIMES_API = `${CONFIG.API_URL}/api/walibi-belgium/wait-times`;

// Nigloland
export const NIGLOLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/nigloland/wait-times`;
export const NIGLOLAND_SCHEDULE_API = `${CONFIG.API_URL}/api/nigloland/schedule`;

// Plopsaland Belgium
export const PLOPSALAND_DEPANNE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/plopsaland-de-panne/wait-times`;

// Walibi Rhône-Alpes
export const WALIBI_RHONE_ALPES_WAIT_TIMES_API = `${CONFIG.API_URL}/api/walibi-rhone-alpes/wait-times`;

// Walibi Holland
export const WALIBI_HOLLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/walibi-holland/wait-times`;

// Thorpe Park
export const THORPE_PARK_WAIT_TIMES_API = `${CONFIG.API_URL}/api/thorpe-park/wait-times`;

// Bellewaerde
export const BELLEWAERDE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/bellewaerde/wait-times`;

// Bobbejaanland
export const BOBBEJAANLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/bobbejaanland/wait-times`;

// Gardaland
export const GARDALAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/gardaland/wait-times`;

// Mirabilandia
export const MIRABILANDIA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/mirabilandia/wait-times`;

// Movie Park Germany
export const MOVIE_PARK_GERMANY_WAIT_TIMES_API = `${CONFIG.API_URL}/api/movie-park-germany/wait-times`;

// Parque de Atracciones de Madrid
export const PARQUE_DE_ATRACCIONES_DE_MADRID_WAIT_TIMES_API = `${CONFIG.API_URL}/api/parque-de-atracciones-de-madrid/wait-times`;

// Asia
export const SHANGHAI_DISNEYLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/shanghai-disneyland/wait-times`;
export const UNIVERSAL_STUDIOS_BEIJING_WAIT_TIMES_API = `${CONFIG.API_URL}/api/universal-studios-beijing/wait-times`;
export const LOTTE_WORLD_WAIT_TIMES_API = `${CONFIG.API_URL}/api/lotte-world/wait-times`;
export const SIX_FLAGS_QIDDIYACITY_WAIT_TIMES_API = `${CONFIG.API_URL}/api/six-flags-qiddiyacity/wait-times`;
export const TOKYO_DISNEYLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/tokyo-disneyland/wait-times`;
export const HONG_KONG_DISNEYLAND_PARK_WAIT_TIMES_API = `${CONFIG.API_URL}/api/hong-kong-disneyland-park/wait-times`;

// Nouveaux parcs
export const ALTON_TOWERS_WAIT_TIMES_API = `${CONFIG.API_URL}/api/alton-towers/wait-times`;
export const UNIVERSAL_STUDIOS_SINGAPORE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/universal-studios-singapore/wait-times`;
export const VOLCANO_BAY_WAIT_TIMES_API = `${CONFIG.API_URL}/api/volcano-bay/wait-times`;
export const LEGOLAND_DEUTSCHLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/legoland-deutschland/wait-times`;
export const TOKYO_DISNEYSEA_WAIT_TIMES_API = `${CONFIG.API_URL}/api/tokyo-disneysea/wait-times`;
export const CAROWINDS_WAIT_TIMES_API = `${CONFIG.API_URL}/api/carowinds/wait-times`;
export const KENNYWOOD_WAIT_TIMES_API = `${CONFIG.API_URL}/api/kennywood/wait-times`;
export const HEIDE_PARK_WAIT_TIMES_API = `${CONFIG.API_URL}/api/heide-park/wait-times`;
export const KNOEBELS_WAIT_TIMES_API = `${CONFIG.API_URL}/api/knoebels/wait-times`;
export const SILVER_DOLLAR_CITY_WAIT_TIMES_API = `${CONFIG.API_URL}/api/silver-dollar-city/wait-times`;
export const LA_RONDE_WAIT_TIMES_API = `${CONFIG.API_URL}/api/la-ronde/wait-times`;
export const KINGS_ISLAND_WAIT_TIMES_API = `${CONFIG.API_URL}/api/kings-island/wait-times`;
export const HERSHEYPARK_WAIT_TIMES_API = `${CONFIG.API_URL}/api/hersheypark/wait-times`;

// Coordonnées complètes des parcs à thème pour l'intégration météo
// Exclut Disneyland Paris (DLP et WDS) comme demandé
export const PARK_COORDINATES = {
  dlp: { lat: 48.8722344, lng: 2.7758079, name: 'Disneyland Paris', country: 'France' },
  wds: { lat: 48.8722344, lng: 2.7758079, name: 'Disney Adventure World', country: 'France' },
  // Europe
  efteling: { lat: 51.6500, lng: 5.0500, name: 'Efteling', country: 'Pays-Bas' },
  phantasialand: { lat: 50.7983, lng: 6.8783, name: 'Phantasialand', country: 'Allemagne' },
  'parc-asterix': { lat: 49.1361, lng: 2.5722, name: 'Parc Astérix', country: 'France' },
  futuroscope: { lat: 46.6700, lng: 0.3700, name: 'Futuroscope', country: 'France' },
  puydufou: { lat: 46.8900, lng: -0.9300, name: 'Le Puy du Fou', country: 'France' },
  'le-pal': { lat: 46.5375, lng: 3.5589, name: 'Le Pal', country: 'France' },
  energylandia: { lat: 49.9873, lng: 19.4214, name: 'Energylandia', country: 'Pologne' },
  'grona-lund': { lat: 59.3233, lng: 18.0967, name: 'Gröna Lund', country: 'Suède' },
  gronaLund: { lat: 59.3233, lng: 18.0967, name: 'Gröna Lund', country: 'Suède' }, // Alias
  nigloland: { lat: 48.1833, lng: 4.6167, name: 'Nigloland', country: 'France' },
  'plopsaland-de-panne': { lat: 51.0833, lng: 2.5833, name: 'Plopsaland Belgium', country: 'Belgique' },
  portaventura: { lat: 41.0856, lng: 1.1556, name: 'PortAventura World', country: 'Espagne' },
  ferrariLand: { lat: 41.0856, lng: 1.1556, name: 'Ferrari Land', country: 'Espagne' },
  toverland: { lat: 51.397931849825966, lng: 5.984305056197651, name: 'Toverland', country: 'Pays-Bas' },
  'walibi-belgium': { lat: 50.7000, lng: 4.5917, name: 'Walibi Belgium', country: 'Belgique' },
  walibiBelgium: { lat: 50.7000, lng: 4.5917, name: 'Walibi Belgium', country: 'Belgique' }, // Alias
  'walibi-rhone-alpes': { lat: 45.621660818127474, lng: 5.569064162607406, name: 'Walibi Rhône-Alpes', country: 'France' },
  'walibi-holland': { lat: 52.4401, lng: 5.7675, name: 'Walibi Holland', country: 'Pays-Bas' },
  'thorpe-park': { lat: 51.4056, lng: -0.5111, name: 'Thorpe Park', country: 'Royaume-Uni' },
  thorpePark: { lat: 51.4056, lng: -0.5111, name: 'Thorpe Park', country: 'Royaume-Uni' }, // Alias
  bellewaerde: { lat: 50.8500, lng: 2.9500, name: 'Bellewaerde', country: 'Belgique' },
  bobbejaanland: { lat: 51.2019, lng: 4.9053, name: 'Bobbejaanland', country: 'Belgique' },
  gardaland: { lat: 45.4556, lng: 10.7167, name: 'Gardaland', country: 'Italie' },
  mirabilandia: { lat: 44.3400, lng: 12.2600, name: 'Mirabilandia', country: 'Italie' },
  'movie-park-germany': { lat: 51.6167, lng: 6.9667, name: 'Movie Park Germany', country: 'Allemagne' },
  movieParkGermany: { lat: 51.6167, lng: 6.9667, name: 'Movie Park Germany', country: 'Allemagne' }, // Alias
  'parque-de-atracciones-de-madrid': { lat: 40.4154, lng: -3.7502, name: 'Parque de Atracciones de Madrid', country: 'Espagne' },
  parqueDeAtraccionesDeMadrid: { lat: 40.4154, lng: -3.7502, name: 'Parque de Atracciones de Madrid', country: 'Espagne' }, // Alias
  'europa-park': { lat: 48.2667, lng: 7.7167, name: 'Europa-Park', country: 'Allemagne' },
  rulantica: { lat: 48.260694127529064, lng: 7.740044354875046, name: 'Rulantica', country: 'Allemagne' },

  // Amérique du Nord
  'magic-kingdom': { lat: 28.417665, lng: -81.581238, name: 'Magic Kingdom', country: 'États-Unis' },
  epcot: { lat: 28.374382, lng: -81.549416, name: 'EPCOT', country: 'États-Unis' },
  'hollywood-studios': { lat: 28.358011, lng: -81.559189, name: "Disney's Hollywood Studios", country: 'États-Unis' },
  'animal-kingdom': { lat: 28.3580, lng: -81.5900, name: "Disney's Animal Kingdom", country: 'États-Unis' },
  'typhoon-lagoon': { lat: 28.3650, lng: -81.5278, name: "Disney's Typhoon Lagoon", country: 'États-Unis' },
  'blizzard-beach': { lat: 28.3518, lng: -81.5754, name: "Disney's Blizzard Beach", country: 'États-Unis' },
  'disneyland-california': { lat: 33.812511, lng: -117.918976, name: 'Disneyland Park', country: 'États-Unis' },
  disneylandCalifornia: { lat: 33.812511, lng: -117.918976, name: 'Disneyland Park', country: 'États-Unis' }, // Alias
  'disney-california-adventure': { lat: 33.806187, lng: -117.920059, name: 'Disney California Adventure', country: 'États-Unis' },
  disneyCaliforniaAdventure: { lat: 33.806187, lng: -117.920059, name: 'Disney California Adventure', country: 'États-Unis' }, // Alias
  'universal-studios-california': { lat: 34.138088, lng: -118.353470, name: 'Universal Studios California', country: 'États-Unis' },
  // Alias court utilisé par currentPark (usc)
  usc: { lat: 34.138088, lng: -118.353470, name: 'Universal Studios California', country: 'États-Unis' },
  'legoland-florida': { lat: 27.99137164932429, lng: -81.69311873786921, name: 'LEGOLAND Florida', country: 'États-Unis' },
  'seaworld-orlando': { lat: 28.4111, lng: -81.4611, name: 'SeaWorld Orlando', country: 'États-Unis' },
  'busch-gardens-tampa': { lat: 28.0372, lng: -82.4194, name: 'Busch Gardens Tampa Bay', country: 'États-Unis' },
  'knotts-berry-farm': { lat: 33.844280, lng: -118.000404, name: "Knott's Berry Farm", country: 'États-Unis' },

  // Asie
  'shanghai-disneyland': { lat: 31.1434, lng: 121.6580, name: 'Shanghai Disneyland', country: 'Chine' },
  shanghaiDisneyland: { lat: 31.1434, lng: 121.6580, name: 'Shanghai Disneyland', country: 'Chine' }, // Alias
  'universal-studios-beijing': { lat: 39.85444650155336, lng: 116.67648494231007, name: 'Universal Studios Beijing', country: 'Chine' },
  universalStudiosBeijing: { lat: 39.85444650155336, lng: 116.67648494231007, name: 'Universal Studios Beijing', country: 'Chine' }, // Alias
  'lotte-world': { lat: 37.5111, lng: 127.0981, name: 'Lotte World', country: 'Corée du Sud' },
  lotteWorld: { lat: 37.5111, lng: 127.0981, name: 'Lotte World', country: 'Corée du Sud' }, // Alias
  'tokyo-disneyland': { lat: 35.6329, lng: 139.8804, name: 'Tokyo Disneyland', country: 'Japon' },
  tokyoDisneyland: { lat: 35.6329, lng: 139.8804, name: 'Tokyo Disneyland', country: 'Japon' }, // Alias
  'hong-kong-disneyland-park': { lat: 22.3132, lng: 114.0413, name: 'Hong Kong Disneyland', country: 'Hong Kong' },
  hongKongDisneylandPark: { lat: 22.3132, lng: 114.0413, name: 'Hong Kong Disneyland', country: 'Hong Kong' }, // Alias
  'six-flags-qiddiyacity': { lat: 24.58897640551116, lng: 46.332452533099726, name: 'Six Flags Qiddiya City', country: 'Arabie Saoudite' },
  sixFlagsQiddiyacity: { lat: 24.58897640551116, lng: 46.332452533099726, name: 'Six Flags Qiddiya City', country: 'Arabie Saoudite' }, // Alias
  'tokyo-disneysea': { lat: 35.6270, lng: 139.8891, name: 'Tokyo DisneySea', country: 'Japon' },
  tokyoDisneysea: { lat: 35.6270, lng: 139.8891, name: 'Tokyo DisneySea', country: 'Japon' }, // Alias
  'universal-studios-singapore': { lat: 1.2540, lng: 103.8239, name: 'Universal Studios Singapore', country: 'Singapour' },
  universalStudiosSingapore: { lat: 1.2540, lng: 103.8239, name: 'Universal Studios Singapore', country: 'Singapour' }, // Alias

  // Nouveaux parcs - Europe
  'alton-towers': { lat: 52.98647385960769, lng: -1.8846978749998118, name: 'Alton Towers', country: 'Royaume-Uni' },
  altonTowers: { lat: 52.98647385960769, lng: -1.8846978749998118, name: 'Alton Towers', country: 'Royaume-Uni' }, // Alias
  'legoland-deutschland': { lat: 48.4249, lng: 10.3003, name: 'LEGOLAND Deutschland', country: 'Allemagne' },
  legolandDeutschland: { lat: 48.4249, lng: 10.3003, name: 'LEGOLAND Deutschland', country: 'Allemagne' }, // Alias
  'heide-park': { lat: 53.0223, lng: 9.8724, name: 'Heide Park', country: 'Allemagne' },
  heidePark: { lat: 53.0223, lng: 9.8724, name: 'Heide Park', country: 'Allemagne' }, // Alias

  // Nouveaux parcs - Amérique du Nord
  'volcano-bay': { lat: 28.4614, lng: -81.4723, name: "Universal's Volcano Bay", country: 'États-Unis' },
  volcanoBay: { lat: 28.4614, lng: -81.4723, name: "Universal's Volcano Bay", country: 'États-Unis' }, // Alias
  'carowinds': { lat: 35.1006, lng: -80.9408, name: 'Carowinds', country: 'États-Unis' },
  carowinds: { lat: 35.1006, lng: -80.9408, name: 'Carowinds', country: 'États-Unis' }, // Alias
  'kennywood': { lat: 40.3866, lng: -79.8619, name: 'Kennywood', country: 'États-Unis' },
  kennywood: { lat: 40.3866, lng: -79.8619, name: 'Kennywood', country: 'États-Unis' }, // Alias
  'knoebels': { lat: 40.8800, lng: -76.5012, name: 'Knoebels Amusement Park', country: 'États-Unis' },
  knoebels: { lat: 40.8800, lng: -76.5012, name: 'Knoebels Amusement Park', country: 'États-Unis' }, // Alias
  'silver-dollar-city': { lat: 36.66779034551362, lng: -93.33848897699254, name: 'Silver Dollar City', country: 'États-Unis' },
  silverDollarCity: { lat: 36.66779034551362, lng: -93.33848897699254, name: 'Silver Dollar City', country: 'États-Unis' }, // Alias
  'la-ronde': { lat: 45.5227, lng: -73.5342, name: 'La Ronde', country: 'Canada' },
  laRonde: { lat: 45.5227, lng: -73.5342, name: 'La Ronde', country: 'Canada' }, // Alias
  'kings-island': { lat: 39.3443, lng: -84.2652, name: 'Kings Island', country: 'États-Unis' },
  kingsIsland: { lat: 39.3443, lng: -84.2652, name: 'Kings Island', country: 'États-Unis' }, // Alias
  'hersheypark': { lat: 40.2878, lng: -76.6591, name: 'Hersheypark', country: 'États-Unis' },
  hersheypark: { lat: 40.2878, lng: -76.6591, name: 'Hersheypark', country: 'États-Unis' } // Alias
};

/**
 * Obtient les coordonnées d'un parc à partir de son path
 * @param {string} path - Le chemin du parc (ex: '/parc-asterix')
 * @returns {object|null} Les coordonnées du parc ou null si non trouvé
 */
export const getParkCoordinates = (path) => {
  const rawPath = path || '';
  if (rawPath === '/' || rawPath === '/shows' || rawPath === '/restaurants' || rawPath.startsWith('/my-pass')) {
    return PARK_COORDINATES.dlp;
  }

  const normalizedPath = rawPath.replace(/\/+$/, '');

  if (normalizedPath === '/disney-adventure-world' || normalizedPath === '/studios' || normalizedPath === '/walt-disney-studios') {
    return PARK_COORDINATES.wds;
  }

  if (normalizedPath === '/plopsaland-belgium') {
    return PARK_COORDINATES['plopsaland-de-panne'];
  }

  // Gestion spéciale pour Ferrari Land
  if (rawPath.includes('/portaventura/ferrari-land')) {
    return PARK_COORDINATES['ferrariLand'];
  }

  // Nettoyer le path pour obtenir la clé
  const parkKey = normalizedPath.replace('/', '').replace(/^\/+/, '');
  return PARK_COORDINATES[parkKey] || null;
};

/**
 * Vérifie si un parc supporte la météo (exclut Disneyland Paris)
 * @param {string} path - Le chemin du parc
 * @returns {boolean} True si le parc supporte la météo
 */
export const isParkWeatherSupported = (path) => {
  // Support spécial pour Ferrari Land
  if (path.includes('/portaventura/ferrari-land')) {
    return true;
  }

  return getParkCoordinates(path) !== null;
};
